import { SectionStatusServant } from './section_status_servant';
import { LinkedSectionsModel } from '../../sections/models/linked_sections_model';
import { Section } from '../../sections/builders/section_builder';
import { RegistrationWaitlistPositionModel } from '../models/registration_waitlist_position_model';
import { ISectionsDatasource } from '../../sections/datasources/sections_datasource';
import { ISectionType } from '../../../interfaces/scheduler_interfaces';
import { ISection } from '../../../interfaces/scheduler_interfaces';
import { TermCourse } from '../../term_courses/builders/term_course_builder';
import { ScheduledSectionStatus } from '../types/scheduled_section_status_enum';
import { TermCourseServant } from '../../term_courses/servants/term_course_servant';
import { ScheduledSectionStatusServant } from './scheduled_section_status_servant';
import { TermScheduleFlags } from '../services/term_schedule_flags';
import * as _4 from 'lodash4';
import { ScheduledSectionTileServant } from './scheduled_section_tile_servant';

export class ScheduledSectionServant {
  static canBeSelected(section: Section | ISection, termScheduleFlags?: TermScheduleFlags) {
    if (section.is_selected) {
      return true;
    }
    return ScheduledSectionServant.isEligibleForSelection(section, termScheduleFlags);
  }

  static isSectionScheduled(section: ISection): boolean {
    return (
      !TermCourseServant.isMultiSectionCourse(section._course) &&
      _4.includes(ScheduledSectionStatus.getScheduledSectionStatuses(), section.status)
    );
  }

  static isEligibleForSelection(section: Section | ISection, termScheduleFlags?: TermScheduleFlags) {
    const notWaitlisted = !_4.get(section, 'is_waitlisted', false);
    if (notWaitlisted) {
      return _4.get(section, '_isEligibleForRegistration', false);
    }

    if (termScheduleFlags && ScheduledSectionServant.isSectionWaitListed(section)) {
      return termScheduleFlags.allowWaitlistedSectionsForRegistrationSelection();
    }

    return ScheduledSectionServant.hasAvailableSeats(section);
  }

  static isSectionWaitListed(section: ISection): boolean {
    return SectionStatusServant.isStatusWaitlisted(section);
  }
  static hasAvailableSeats(section: ISection) {
    // Scheduled sections might now have section._availability.is_seat_available flag.
    // if unlimited seating - dont check for seat availability.
    if (section.is_unlimited_seating) {
      return true;
    }
    if (section._availability) {
      if (!section._availability.is_seat_available) {
        return false;
      }
      if (!_4.isUndefined(section._availability.avail_seats)) {
        return section._availability.avail_seats > 0;
      } else {
        return !SectionStatusServant.isStatusWaitlisted(section);
      }
    } else {
      return ScheduledSectionServant.isSectionScheduled(section);
    }
  }
  constructor(
    private ngSectionsDatasource: ISectionsDatasource,
    private ngTermCourseServant: TermCourseServant,
    private ngScheduledSectionTileServant: ScheduledSectionTileServant,
    private ngTermScheduleFlags: TermScheduleFlags,
    private ngRegistrationWaitlistPositionModel: RegistrationWaitlistPositionModel,
    private ngScheduledSectionStatusServant: ScheduledSectionStatusServant,
    private ngLinkedSectionsModel: LinkedSectionsModel
  ) {}

  // TODO: Think about moving this to multi_section_servant
  isSectionScheduledMultiSection(section: ISection): boolean {
    return (
      TermCourseServant.isMultiSectionCourse(section._course) &&
      _4.includes(ScheduledSectionStatus.getScheduledSectionStatuses(), section.status)
    );
  }

  isSectionScheduled_(section: ISection): boolean {
    return _4.includes(ScheduledSectionStatus.getScheduledSectionStatuses(), section.status);
  }

  isSectionRegistered(section: ISection): boolean {
    const registeredSectionStatuses: ScheduledSectionStatus[] = ScheduledSectionStatus.getRegisteredSectionStatuses();
    return _4.includes(registeredSectionStatuses, section.status);
  }

  isSectionInProgress(section: ISection): boolean {
    return this.ngScheduledSectionStatusServant.isStatusInProgress(section);
  }

  canBeRegistered(section: ISection): boolean {
    return (
      !(
        this.ngScheduledSectionStatusServant.isStatusRegisteredOrCompleteOrInProgress(section) ||
        SectionStatusServant.isStatusWaitlisted(section) ||
        this.isSectionWithdrawn(section)
      ) || this.canAddFromWaitlist(section)
    );
  }

  canBeWaitlisted(section: ISection): boolean {
    const isWaitlistEnabled = this.ngScheduledSectionTileServant.isWaitlistEnabled();
    const canSectionBeRegistered = this.canBeRegistered(section);
    const canSectionBeWaitlisted = isWaitlistEnabled && !this.isSeatAvailable(section) && canSectionBeRegistered;
    return canSectionBeWaitlisted;
  }

  isNonMultiSectionRegistered(section: ISection): boolean {
    return !TermCourseServant.isMultiSectionCourse(section._course) && this.isSectionRegistered(section);
  }

  isMultiSectionRegistered(section: ISection): boolean {
    return TermCourseServant.isMultiSectionCourse(section._course) && this.isSectionRegistered(section);
  }

  isSectionTypeScheduled(sectionType: ISectionType) {
    return (
      _4.size(
        _4.filter(
          _4.toArray(this.ngSectionsDatasource.read()),
          (section: ISection) =>
            section.status !== ScheduledSectionStatus.UNSCHEDULED &&
            section.section_type === sectionType.id &&
            section.status !== ScheduledSectionStatus.WITHDRAWN
        )
      ) > 0
    );
  }

  isSectionWithdrawn(section: ISection): boolean {
    return this.ngScheduledSectionStatusServant.isStatusWithdrawn(section);
  }

  isSectionMultiSection(section: ISection): boolean {
    const course = _4.get<ISection['_course']>(section, '_course');
    if (!course) {
      return false;
    }
    return TermCourseServant.isMultiSectionCourse(course);
  }

  isAutoWaitlistedHidePosition(section: ISection) {
    const waitlistPosition = this.ngRegistrationWaitlistPositionModel.get(section.id);
    return (
      SectionStatusServant.isStatusWaitlisted(section) &&
      this.isAutoWailistSection(section) &&
      !_4.isUndefined(waitlistPosition) &&
      !this.isSeatAvailable(section) &&
      waitlistPosition.place_in_waitlist === 0
    );
  }
  isWaitlistedEligibleForRegistration(section: ISection) {
    // Whats the difference between position and place_in_waitlist?
    // position is absolute waitlist value returned by SIS. place_in_waitlist is the relative waitlist position calculated by us
    const waitlistPosition = this.ngRegistrationWaitlistPositionModel.get(section.id);
    if (this.isAutoWaitlistedHidePosition(section)) {
      return false;
    }
    const placeInWaitlist = (waitlistPosition && waitlistPosition.place_in_waitlist) || 0;
    if (waitlistPosition && _4.get(section, '_availability.is_seat_available', false)) {
      return placeInWaitlist <= section._availability.avail_seats;
    } else {
      return false;
    }
  }
  canAddFromWaitlist(section: ISection): boolean {
    // Udpate for SGD-11593
    // const oldConditionForProperWaitlistCheck = (SectionStatusServant.isStatusWaitlisted(section) && !this.ngTermScheduleFlags.isBatchRegisterWaitlistedStudentsEnabled() &&
    // this.hasAvailableSeats(section) &&
    // this.isWaitlistedEligibleForRegistration(section));

    const allowStudentsToSelectForRegister =
      SectionStatusServant.isStatusWaitlisted(section) &&
      !this.ngTermScheduleFlags.isBatchRegisterWaitlistedStudentsEnabled(); // When this is off(false)
    return allowStudentsToSelectForRegister;
  }

  isSeatAvailable(section: ISection) {
    return _4.get(section, '_availability.is_seat_available', false);
  }
  hasLinkedSections(sectionId: number) {
    return _4.isEmpty(this.ngLinkedSectionsModel.get(sectionId));
  }
  getLinkedSections(sectionId: number, sections: ISection[]) {
    const linkedSectionIds = this.ngLinkedSectionsModel.get(sectionId);
    return _4.compact(_4.map(linkedSectionIds, (lnkdSctnId) => _4.find(sections, (sctn) => sctn.id === lnkdSctnId)));
  }
  getRegisteredLinkedSections(sectionId: number, sections: ISection[]) {
    return _4.filter(this.getLinkedSections(sectionId, sections), (sctn) => this.isSectionRegistered(sctn));
  }
  isAutoWailistSection(section: ISection) {
    return _4.get(section, '_availability.is_auto_waitlist', false);
  }

  getAutoWaitlistSections(sections: ISection[]) {
    return sections
      .filter((selectedSection) => this.isAutoWailistSection(selectedSection))
      .map((autoWaitlistedSection) => autoWaitlistedSection.id);
  }

  getSectionEnforcedCoreqs(section: ISection) {
    return _4.map(_4.get(section, '_course._coreq_courses', []), (coreqCourse: TermCourse) => coreqCourse.id);
  }

  isCoreq(section: ISection, allRegistrationSections: ISection[]): boolean {
    const sectionsWithCoreqs = _4.filter(allRegistrationSections, (sct: ISection) =>
      this.ngScheduledSectionStatusServant.hasEnforcedCoreqs(sct)
    );
    const allCoreqCourses = _4.map(sectionsWithCoreqs, (sectionWithCoreqs) => sectionWithCoreqs._course._coreq_courses);
    const allCoreqCoursesIds = _4.map(_4.flatten(allCoreqCourses), (coreqCourse: TermCourse) => coreqCourse.id);

    return _4.some(allCoreqCoursesIds, (coreqCourseId) => _4.isEqual(section._course.id, coreqCourseId));
  }

  getCoreqsRelated(section: ISection, allRegistrationSections: ISection[]) {
    const coreqsContainedBySection = _4.get<ISection['_course']['_coreq_courses']>(
      section,
      '_course._coreq_courses',
      []
    );
    const hasCoreqs = !_4.isEmpty(coreqsContainedBySection);
    // if has coreqs is a coreq `parent`
    if (hasCoreqs) {
      return _4.filter(allRegistrationSections, (regSection) =>
        _4.includes(_4.map(coreqsContainedBySection, 'id'), regSection._course.id)
      );
    } else if (this.isCoreq(section, allRegistrationSections)) {
      // if it's a coreq but does not contain coreqs has a `parent` and could have `siblings`
      const sectionsWithCoreqs = _4.filter(allRegistrationSections, (sct: ISection) =>
        this.ngScheduledSectionStatusServant.hasEnforcedCoreqs(sct)
      );
      const allCoreqCourses = _4.map(
        sectionsWithCoreqs,
        (sectionWithCoreqs) => sectionWithCoreqs._course._coreq_courses
      );
      const parentCoreq = _4.find(sectionsWithCoreqs, (sectionWithCoreqs) =>
        _4.some(sectionWithCoreqs._course.coreq, (courseId) => _4.isEqual(courseId, section._course.id))
      );
      const siblingsCoreqsIds = _4.filter(
        _4.get(parentCoreq, '_course.coreq', []),
        (coreqId) => !_4.isEqual(coreqId, section._course.id)
      );
      const siblingsCoreqCourses = _4.filter(allRegistrationSections, (regSection) =>
        _4.includes(siblingsCoreqsIds, regSection._course.id)
      );

      return _4.concat(siblingsCoreqCourses, [parentCoreq]);
    } else {
      return [];
    }
  }

  getRegisteredCoreqsRelated(section: ISection, allRegistrationSections: ISection[]) {
    const coreqsCoursesRelated: any[] = this.getCoreqsRelated(section, allRegistrationSections);
    const registeredCoreqs = _4.filter(coreqsCoursesRelated, (coreqsCourseRelated: any) =>
      _4.some(
        this.ngSectionsDatasource.read(),
        (sctn) => sctn.course === coreqsCourseRelated.course && this.isSectionRegistered(sctn)
      )
    );
    return _4.filter(registeredCoreqs, (coreqSection: ISection) => !this.isSectionWithdrawn(coreqSection));
  }
  getInProgressCoreqsRelated(section: ISection, allRegistrationSections: ISection[]) {
    return this.getRegisteredCoreqsRelated(section, allRegistrationSections);
  }
  getScheduledSectionByCD(cd: string) {
    return _4.find(this.ngSectionsDatasource.read(), (item: ISection) => item.cd === cd && item.status !== '');
  }

  getUnscheduleCourseMenuOption(options: any[], title: string, menuEnum: any) {
    return options.push({
      id: menuEnum.UNSCHEDULE_COURSE,
      text: `Unschedule ${title}`
    });
  }
  getDropCourseMenuOption(options: any[], title: string, menuEnum: any) {
    return options.push({
      id: menuEnum.DROP_COURSE,
      text: `Drop ${title}`
    });
  }
  getRemoveWaitlistMenuOption(options: any[], menuEnum: any) {
    return options.push({
      id: menuEnum.REMOVE_WAITLIST,
      text: `Remove from waitlist`
    });
  }
  getUnscheduleSectionsMenuOption(options: any[], title: string, menuEnum: any) {
    return options.push({
      id: menuEnum.UNSCHEDULE_SECTIONS,
      text: `Unschedule ${title}`
    });
  }
  getDropSectionsMenuOption(options: any[], title: string, menuEnum: any) {
    return options.push({
      id: menuEnum.DROP_SECTIONS,
      text: `Drop ${title}`
    });
  }
  getDropEnforcedCoreqsMenuOption(options: any[], title: string, menuEnum: any) {
    return options.push({
      id: menuEnum.DROP_ENFORCED_COREQS,
      text: `${title}`
    });
  }
  getSwapSectionMenuOption(options: any[], menuEnum: any) {
    return options.push({
      id: menuEnum.SWAP_SECTION,
      text: `Swap Time`
    });
  }
  getSwapCourseMenuOption(options: any[], menuEnum: any) {
    return options.push({
      id: menuEnum.SWAP_COURSE,
      text: `Swap Course`
    });
  }
  getScheduleAgainMenuOption(options: any[], menuEnum: any) {
    return options.push({
      id: menuEnum.SCHEDULE_AGAIN,
      text: `Schedule Another Time`
    });
  }

  getWithdrawMenuOption(options: any[], menuEnum: any) {
    return options.push({
      id: menuEnum.WITHDRAW,
      text: `Withdraw`
    });
  }

  isInRescheduleMode($state: ng.ui.IStateService) {
    return _4.includes($state.current.name || '', 'reschedule');
  }

  hasUnsatisfiedCrossCourseCoReq(section: ISection) {
    return (
      _4.get(section, '_section_status.has_linked_section_coreqs') &&
      !_4.get(section, '_section_status.linked_section_coreqs_satisfied')
    );
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/course_scheduler/servants/scheduled_section_servant.ts